import { AccordionHeader as AccordionHeaderRadix, AccordionTrigger } from '@radix-ui/react-accordion';
import cn from 'classnames';
import { forwardRef } from 'react';

import { ChevronDownIcon } from '../../../foundations';
import { useAccordionGroupContext } from '../accordion-group/accordion-group-context';

import type { ForwardedRef, ReactElement } from 'react';

import type { AccordionHeaderProps } from './accordion-header.types';

import './accordion-header.scss';

/**
 * The `AccordionHeader` component defines the clickable header of the accordion.
 * When clicked, it toggles the visibility of the `AccordionContent`.
 *
 * ### Usage
 * Place the `AccordionHeader` component inside an `Accordion` component to define the header of the accordion.
 *
 * ```tsx
 * import { AccordionGroup, Accordion, AccordionHeader, AccordionContent } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <AccordionGroup>
 *       <Accordion value="banana-facts">
 *         <AccordionHeader>Banana facts</AccordionHeader>
 *         <AccordionContent>
 *           Bananas are a great source of potassium and can help replenish electrolytes after exercise.
 *         </AccordionContent>
 *       </Accordion>
 *       <Accordion>...</Accordion>
 *       <Accordion>...</Accordion>
 *     </AccordionGroup>
 *   );
 * }
 * ```
 */
const AccordionHeader = forwardRef(
  (props: AccordionHeaderProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
    const {
      children,
      className,
      description,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const accordionGroupContextValue = useAccordionGroupContext();
    if (accordionGroupContextValue === null) {
      throw new Error('No AccordionContext provided');
    }

    const { styling } = accordionGroupContextValue;

    const classes = cn(
      'rbui-accordion-header',
      {
        'rbui-accordion-header--styled': styling === 'styled',
      },
      className,
    );

    return (
      <AccordionHeaderRadix asChild className={classes} ref={forwardedRef} tabIndex={0} {...rest}>
        <AccordionTrigger>
          <div className="rbui-accordion-header__inner">
            <div
              className={cn(
                'rbui-accordion-header__title',
                styling === 'styled' && 'rbui-accordion-header__title--styled',
              )}
            >
              {children}
            </div>
            {description && <div className="rbui-accordion-header__description">{description}</div>}
          </div>

          <ChevronDownIcon aria-hidden className="rbui-accordion-header__chevron" />
        </AccordionTrigger>
      </AccordionHeaderRadix>
    );
  },
);

AccordionHeader.displayName = 'AccordionHeader';

export default AccordionHeader;
